import React, { useState, useEffect } from 'react';
import { TextField, Box, FormControlLabel, Checkbox, Typography } from '@mui/material';
import {
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  CancelButton,
  SaveButton
} from './styles';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../../../store';
import { createSuperAdminDocumentType, updateSuperAdminDocumentType } from '../../../../../../modules/superadmin/actions';
import { DocumentType } from '../../../../../../modules/superadmin/types';

interface DocumentTypeModalProps {
  open: boolean;
  onClose: () => void;
  documentType?: DocumentType | null; // If provided, we are in Edit mode
}

const DocumentTypeModal: React.FC<DocumentTypeModalProps> = ({ open, onClose, documentType }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isExpiryRequired, setIsExpiryRequired] = useState(false);
  const [isIssueDateRequired, setIsIssueDateRequired] = useState(false);
  const [isMandatory, setIsMandatory] = useState(false);

  const isCreateSuccess = useSelector((state: RootState) => state.superadmin.createDocumentTypeSuccess);
  const isUpdateSuccess = useSelector((state: RootState) => state.superadmin.updateDocumentTypeSuccess);

  useEffect(() => {
    if (documentType) {
      setName(documentType.document_type || '');
      setDescription(documentType.description || '');
      setIsExpiryRequired(documentType.is_expiry_required || false);
      setIsIssueDateRequired(documentType.is_issue_date_required || false);
      setIsMandatory(documentType.is_mandatory || false);
    } else {
      setName('');
      setDescription('');
      setIsExpiryRequired(false);
      setIsIssueDateRequired(false);
      setIsMandatory(false);
    }
  }, [documentType, open]);

  useEffect(() => {
    if (isCreateSuccess || isUpdateSuccess) {
      onClose();
    }
  }, [isCreateSuccess, isUpdateSuccess, onClose]);

  const [nameError, setNameError] = useState(false);

  const handleSave = () => {
    const isNameValid = !!name.trim();
    setNameError(!isNameValid);

    if (!isNameValid) {
      return;
    }

    const payload = {
      document_type: name,
      description: description,
      is_expiry_required: isExpiryRequired,
      is_issue_date_required: isIssueDateRequired,
      is_mandatory: isMandatory,
    };

    if (documentType) {
      dispatch(updateSuperAdminDocumentType({
        document_type_id: documentType.document_type_id,
        ...payload
      }));
    } else {
      dispatch(createSuperAdminDocumentType(payload));
    }
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <StyledDialogTitle>
        {documentType ? 'Edit Document Type' : 'Create Document Type'}
      </StyledDialogTitle>
      <StyledDialogContent>
        <Box sx={{ mt: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            required
            fullWidth
            value={name}
            error={nameError}
            helperText={nameError ? 'Name is required' : ''}
            onChange={(e) => {
              setName(e.target.value);
              if (e.target.value.trim()) setNameError(false);
            }}
          />
          <TextField
            margin="dense"
            label="Description"
            multiline
            rows={3}
            fullWidth
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            sx={{ mt: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isIssueDateRequired}
                  onChange={(e) => setIsIssueDateRequired(e.target.checked)}
                  sx={{ color: '#5E8E7E', '&.Mui-checked': { color: '#5E8E7E' } }}
                />
              }
              label={<Typography variant="body2">Required Issue Date</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isExpiryRequired}
                  onChange={(e) => setIsExpiryRequired(e.target.checked)}
                  sx={{ color: '#5E8E7E', '&.Mui-checked': { color: '#5E8E7E' } }}
                />
              }
              label={<Typography variant="body2">Required Expiry Date</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isMandatory}
                  onChange={(e) => setIsMandatory(e.target.checked)}
                  sx={{ color: '#5E8E7E', '&.Mui-checked': { color: '#5E8E7E' } }}
                />
              }
              label={<Typography variant="body2">Is Mandatory</Typography>}
            />
          </Box>
        </Box>
      </StyledDialogContent>
      <StyledDialogActions>
        <CancelButton onClick={onClose}>
          Cancel
        </CancelButton>
        <SaveButton onClick={handleSave} variant="contained" disableElevation>
          {documentType ? 'Save Changes' : 'Create'}
        </SaveButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default DocumentTypeModal;
